function emit({ config, environment, event, listenerManager, }) {
    const { url, token, mode } = config;
    const isEnabled = mode !== "disabled";
    if (isEnabled) {
        listenerManager.call(event);
        environment.send(url, token, event);
    }
}

var REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

function createClientIdManager(environmentManager) {
    return {
        getClientId: () => {
            const key = "visitorId";
            const environment = environmentManager.get();
            const storage = environment.storage;
            const existingClientId = storage.getItem(key);
            const clientId = existingClientId && validate(existingClientId)
                ? existingClientId
                : environment.generateUUID();
            storage.setItem(key, clientId);
            return clientId;
        },
    };
}

const version = "0.7.10";

function getEventConfig(config) {
    const { trackingId } = config;
    return { trackingId };
}
function getSource(config) {
    return (config.source || []).concat([`relay@${version}`]);
}
function createMeta(type, config, environment, clientIdManager) {
    const { getReferrer, getLocation, getUserAgent } = environment;
    const eventConfig = getEventConfig(config);
    const clientId = clientIdManager.getClientId();
    return Object.freeze({
        type,
        config: eventConfig,
        ts: Date.now(),
        source: getSource(config),
        clientId,
        userAgent: getUserAgent(),
        referrer: getReferrer(),
        location: getLocation(),
    });
}

function createRelayEvent(type, payload, config, environment, clientIdManager) {
    return {
        ...payload,
        meta: createMeta(type, config, environment, clientIdManager),
    };
}

const ANY_EVENT_TYPE = "*";
function createListenerManager() {
    const listeners = [];
    function getListenerIndex({ type, callback }) {
        return listeners.findIndex((listener) => listener.type === type && listener.callback === callback);
    }
    function isMatchesType(listener, type) {
        return listener.type === "*" || type === listener.type;
    }
    function add(listener) {
        if (getListenerIndex(listener) < 0) {
            listeners.push(listener);
        }
        return () => remove(listener.type, listener.callback);
    }
    function call(event) {
        listeners.forEach((listener) => {
            if (isMatchesType(listener, event.meta.type)) {
                try {
                    listener.callback(event);
                }
                catch (e) {
                    console.error(e);
                }
            }
        });
    }
    function removeMultiple(type) {
        if (type === ANY_EVENT_TYPE) {
            listeners.length = 0;
        }
        else {
            for (let i = listeners.length - 1; i >= 0; i--) {
                if (listeners[i].type === type) {
                    listeners.splice(i, 1);
                }
            }
        }
    }
    function removeOne(listener) {
        const index = getListenerIndex(listener);
        if (index >= 0) {
            listeners.splice(index, 1);
        }
    }
    function remove(type, callback) {
        callback ? removeOne({ type, callback }) : removeMultiple(type);
    }
    return {
        add,
        call,
        remove,
    };
}

function pick({ url, token, trackingId, ...rest }) {
    return Object.freeze({
        url,
        token,
        trackingId,
        ...(!!rest.mode && { mode: rest.mode }),
        ...(!!rest.source && { source: rest.source }),
    });
}
function createConfigManager(initialConfig) {
    let _config = pick(initialConfig);
    return {
        get: () => _config,
        update: (updatedConfig) => {
            _config = pick({ ..._config, ...updatedConfig });
        },
    };
}

function createExplorerMessenger() {
  const isBrowser = typeof window !== "undefined";
  return {
    sendMessage(message) {
      isBrowser && window.postMessage(message, "*");
    }
  };
}

const cookieManager = createCookieManager();
function createCookieManager() {
    const prefix = "coveo_";
    const getDomain = (host) => {
        const parts = host.split(".").slice(-2);
        return parts.length == 2 ? parts.join(".") : "";
    };
    return {
        getItem(key) {
            const cookiePrefix = `${prefix}${key}=`;
            const cookieArray = document.cookie.split(";");
            for (const cookie of cookieArray) {
                const prettifyCookie = cookie.replace(/^\s+/, "");
                if (prettifyCookie.lastIndexOf(cookiePrefix, 0) === 0) {
                    return prettifyCookie.substring(cookiePrefix.length, prettifyCookie.length);
                }
            }
            return null;
        },
        setItem(key, data, expire) {
            const domain = getDomain(window.location.hostname);
            const expireSection = `;expires=${new Date(new Date().getTime() + expire).toUTCString()}`;
            const domainSection = domain ? `;domain=${domain}` : "";
            document.cookie = `${prefix}${key}=${data}${expireSection}${domainSection};path=/;SameSite=Lax`;
        },
        removeItem(key) {
            this.setItem(key, "", -1);
        },
    };
}

function createBrowserStorage() {
    return {
        getItem(key) {
            return cookieManager.getItem(key) || localStorage.getItem(key);
        },
        removeItem(key) {
            cookieManager.removeItem(key);
            localStorage.removeItem(key);
        },
        setItem(key, data) {
            const oneYear = 31556952000;
            localStorage.setItem(key, data);
            cookieManager.setItem(key, data, oneYear);
        },
    };
}

function getReferrer() {
    const referrer = document.referrer;
    return referrer === "" ? null : referrer;
}
function buildBrowserEnvironment() {
    return {
        runtime: "browser",
        send: (url, token, event) => {
            const response = navigator.sendBeacon(`${url}?access_token=${token}`, new Blob([JSON.stringify([event])], {
                type: "application/json",
            }));
            const messenger = createExplorerMessenger();
            messenger.sendMessage({ kind: "EVENT_PROTOCOL", event, url, token });
            if (!response) {
                throw new Error(`Failed to send the event(s) because the payload size exceeded the maximum allowed size (32 KB). Please contact support if the problem persists.`);
            }
        },
        getReferrer: () => getReferrer(),
        getLocation: () => window.location.href,
        getUserAgent: () => navigator.userAgent,
        generateUUID: () => crypto.randomUUID(),
        storage: createBrowserStorage(),
    };
}

// From: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
function localStorageAvailable() {
    try {
        const x = "__storage_test__";
        localStorage.setItem(x, x);
        localStorage.removeItem(x);
        return true;
    }
    catch (e) {
        return (e instanceof DOMException &&
            e.name === "QuotaExceededError" &&
            // acknowledge QuotaExceededError only if there's something already stored
            localStorage &&
            localStorage.length !== 0);
    }
}

function createNullStorage() {
    return {
        getItem() {
            return null;
        },
        removeItem() {
            return;
        },
        setItem() {
            return;
        },
    };
}

function buildNullEnvironment() {
    return {
        runtime: "null",
        send: () => undefined,
        getReferrer: () => null,
        getLocation: () => null,
        getUserAgent: () => null,
        generateUUID: () => "",
        storage: createNullStorage(),
    };
}

function buildEnvironment(configManager) {
    const active = configManager.get().mode !== "disabled";
    if (active && isBrowser() && localStorageAvailable()) {
        return buildBrowserEnvironment();
    }
    return buildNullEnvironment();
}
function isBrowser() {
    try {
        return typeof window === "object";
    }
    catch (e) {
        return false;
    }
}
function createEnvironmentManager(configManager) {
    return {
        get: () => Object.freeze(buildEnvironment(configManager)),
    };
}

/**
 * Initializes the Relay library object.
 * @param {RelayConfig} initialConfig
 * @returns {Relay}
 */
function createRelay(initialConfig) {
    const configManager = createConfigManager(initialConfig);
    const listenerManager = createListenerManager();
    const environmentManager = createEnvironmentManager(configManager);
    const clientIdManager = createClientIdManager(environmentManager);
    return {
        emit: (type, payload) => {
            const config = configManager.get();
            const environment = environmentManager.get();
            const event = createRelayEvent(type, payload, config, environment, clientIdManager);
            return emit({
                config,
                environment,
                event,
                listenerManager,
            });
        },
        getMeta: (type) => createMeta(type, configManager.get(), environmentManager.get(), clientIdManager),
        on: (type, callback) => listenerManager.add({ type, callback }),
        off: (type, callback) => listenerManager.remove(type, callback),
        updateConfig: (config) => configManager.update(config),
        version,
    };
}

export { createRelay };
